/**
 * Cart drawer "bundle moment" + sticker auto-add + funnel events
 * (ROI 2026-09-12 — ads memo 9/12 12:00, items 2 + 3).
 *
 * Document-delegated on purpose: the drawer section is swapped via innerHTML
 * on every cart change, so nothing here may live inside the drawer markup.
 * Also covers the /cart page, which renders the same progress bar + chips.
 *
 * 1. One-tap chips (snippets/roi-cart-chips.liquid, inside the progress bar).
 *    [data-roi-chip] carries a server-resolved variant → POST /cart/add.js,
 *    then the same synthetic shopify:cart:lines-update event the pack builder
 *    uses, so Horizon's cart-items component refetches the drawer and the
 *    cart icon updates. A chip with no size match renders as a "Pick size"
 *    card whose header tap reveals per-size buttons (each a normal chip).
 * 2. Sticker auto-add. The progress bar reports data-roi-eligible (Mix &
 *    Match units), data-roi-gift (Mystery 3-Pack in cart, 1/0) and
 *    data-roi-gift-variant. At 2+ units with no gift the $0 pack is added
 *    once. If the shopper removes it, it is not re-added this session; if
 *    units drop under 2 and WE added it, it is taken back out so "free with
 *    any 2" stays honest. The Liquid "Add it" prompt remains as the no-JS
 *    fallback (it only renders when the gift is missing).
 * 3. Events via window.roiTrack (layout/theme.liquid): drawer_chip_added,
 *    drawer_gift_auto_added, drawer_gift_auto_removed, drawer_checkout_clicked.
 */
(function () {
  'use strict';
  if (window.__roiCartBundle) return;
  window.__roiCartBundle = true;

  var AUTO_KEY = 'roi_gift_auto';
  var REMOVED_KEY = 'roi_gift_removed';
  var busy = false;
  var seenGiftIn = false;
  var lastGiftOp = 0;
  var timer = null;

  function track(name, data) {
    try {
      if (typeof window.roiTrack === 'function') window.roiTrack(name, data);
    } catch (e) {}
  }
  function flag(k) {
    try {
      return sessionStorage.getItem(k) === '1';
    } catch (e) {
      return false;
    }
  }
  function setFlag(k, on) {
    try {
      if (on) sessionStorage.setItem(k, '1');
      else sessionStorage.removeItem(k);
    } catch (e) {}
  }

  function barState() {
    var b = document.querySelector('.roi-cartbar[data-roi-eligible]');
    if (!b) return null;
    return {
      eligible: parseInt(b.getAttribute('data-roi-eligible') || '0', 10) || 0,
      giftIn: b.getAttribute('data-roi-gift') === '1',
      giftVariant: b.getAttribute('data-roi-gift-variant') || '',
      count: parseInt(b.getAttribute('data-roi-count') || '0', 10) || 0,
      total: parseInt(b.getAttribute('data-roi-total') || '0', 10) || 0,
    };
  }

  // Same synthetic event assets/roi-pack-pdp.js dispatches after its batch
  // add: cart-items refetches the section, cart-icon reads detail.itemCount.
  // action 'update' (not 'add') for the gift so the drawer never auto-opens
  // on a page load that merely back-fills the sticker.
  function announce(action, cart) {
    var ev = new Event('shopify:cart:lines-update', { bubbles: true });
    /** @type {any} */ (ev).action = action;
    /** @type {any} */ (ev).promise = Promise.resolve({
      cart: null,
      detail: { itemCount: cart ? cart.item_count : undefined, didError: false },
    });
    document.dispatchEvent(ev);
  }

  function cartJson() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  var JSON_HEADERS = {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    Accept: 'application/json',
  };

  function addLine(variantId, action) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (d) {
            throw new Error((d && (d.description || d.message)) || 'Could not add that.');
          });
        }
        return res.json();
      })
      .then(function () {
        return cartJson().catch(function () {
          return null;
        });
      })
      .then(function (cart) {
        announce(action || 'add', cart);
        return cart;
      });
  }

  /* ---------- 1. chips ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target : null;
    if (!t) return;
    var chip = t.closest('[data-roi-chip]');
    if (chip) {
      e.preventDefault();
      if (busy || chip.hasAttribute('disabled')) return;
      var variantId = chip.getAttribute('data-variant');
      if (!variantId) return;
      var state = barState() || {};
      var host = chip.closest('[data-roi-chips]');
      var note = host ? host.querySelector('[data-roi-chips-error]') : null;
      busy = true;
      chip.setAttribute('disabled', '');
      chip.classList.add('is-adding');
      if (note) note.hidden = true;
      addLine(variantId, 'add')
        .then(function () {
          track('drawer_chip_added', {
            variant_id: variantId,
            product_id: chip.getAttribute('data-product-id'),
            handle: chip.getAttribute('data-handle'),
            size: chip.getAttribute('data-size'),
            eligible_before: state.eligible,
          });
        })
        .catch(function (err) {
          chip.removeAttribute('disabled');
          chip.classList.remove('is-adding');
          if (note) {
            note.textContent = err && err.message ? err.message : 'Something went wrong. Try again.';
            note.hidden = false;
          }
        })
        .then(function () {
          busy = false;
          schedule();
        });
      return;
    }
    var toggle = t.closest('[data-roi-chip-toggle]');
    if (toggle) {
      e.preventDefault();
      var card = toggle.closest('[data-roi-chip-sizes]');
      var row = card ? card.querySelector('[data-roi-chip-size-row]') : null;
      if (!row) return;
      var open = row.hidden;
      row.hidden = !open;
      card.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  });

  /* ---------- 2. sticker auto-add ---------- */
  function addGift(s) {
    busy = true;
    lastGiftOp = Date.now();
    addLine(s.giftVariant, 'update')
      .then(function () {
        setFlag(AUTO_KEY, true);
        track('drawer_gift_auto_added', { eligible: s.eligible, variant_id: s.giftVariant });
      })
      .catch(function () {})
      .then(function () {
        busy = false;
      });
  }

  function removeGift(s) {
    busy = true;
    lastGiftOp = Date.now();
    cartJson()
      .then(function (cart) {
        var line = (cart.items || []).filter(function (i) {
          return String(i.variant_id) === String(s.giftVariant);
        })[0];
        if (!line) return null;
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify({ id: line.key, quantity: 0 }),
        })
          .then(function (r) {
            return r.json();
          })
          .then(function () {
            return cartJson().catch(function () {
              return null;
            });
          })
          .then(function (cart2) {
            announce('update', cart2);
            setFlag(AUTO_KEY, false);
            seenGiftIn = false;
            track('drawer_gift_auto_removed', { eligible: s.eligible, variant_id: s.giftVariant });
          });
      })
      .catch(function () {})
      .then(function () {
        busy = false;
      });
  }

  function check() {
    var s = barState();
    if (!s || !s.giftVariant || busy) return;
    if (s.giftIn) {
      seenGiftIn = true;
      // Earned it, then dropped back to one item: take our gift back out.
      if (s.eligible < 2 && flag(AUTO_KEY) && Date.now() - lastGiftOp > 1500) removeGift(s);
      return;
    }
    if (s.eligible < 2) return;
    if (seenGiftIn && flag(AUTO_KEY) && Date.now() - lastGiftOp > 4000) {
      // We added it, it was in the cart, and now it's gone while still
      // earned: the shopper removed it on purpose. Respect that all session.
      setFlag(REMOVED_KEY, true);
      setFlag(AUTO_KEY, false);
      seenGiftIn = false;
      return;
    }
    if (flag(REMOVED_KEY)) return;
    addGift(s);
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(check, 250);
  }

  function observe() {
    if (typeof MutationObserver === 'undefined') return;
    var targets = [document.getElementById('cart-drawer'), document.querySelector('.cart-page')].filter(Boolean);
    targets.forEach(function (target) {
      new MutationObserver(schedule).observe(target, { childList: true, subtree: true });
    });
  }

  /* ---------- 3. checkout click (capture: fires before the form submits) ---------- */
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('.cart__checkout-button, button[name="checkout"]') : null;
      if (!btn) return;
      var s = barState() || {};
      track('drawer_checkout_clicked', {
        item_count: s.count,
        eligible_units: s.eligible,
        total: (s.total || 0) / 100,
        surface: btn.closest('#cart-drawer') ? 'drawer' : 'cart-page',
      });
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      observe();
      schedule();
    });
  } else {
    observe();
    schedule();
  }
})();
